import { useState, useRef, useEffect } from "react";
import {
  Home, Phone, ClipboardCheck, Compass, Building2, Truck,
  Calendar, Mail, MessageSquare, FileText, DollarSign, Settings,
  Briefcase, GraduationCap, Shield, Crown, ChevronDown, RefreshCw,
  LayoutGrid, HelpCircle, Send, BarChart2,
  AlertTriangle, TrendingUp, TrendingDown, ArrowRight, Users, Info,
  UserPlus, FilePlus2
} from "lucide-react";
import TaskNotificationBadge from "./TaskNotificationBadge";
import { MonEspaceButton } from "./MonEspaceButton";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;
const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, { ...opts, credentials: "include", headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

// Visual identity — matches DASHBOARD_COMPONENTS from App.js home page
const POLE_IDENTITY = {
  home:               { label: "Accueil",             desc: "SAN-IA CRM",                    icon: Home,           iconGrad: "from-amber-500 to-yellow-500",    bgGrad: "from-amber-950/60 to-slate-950",   text: "text-amber-300",   accent: "#F59E0B" },
  telepro:            { label: "Telepro",             desc: "Prospection telephonique",       icon: Phone,          iconGrad: "from-blue-500 to-indigo-500",     bgGrad: "from-blue-950/60 to-slate-950",    text: "text-blue-300",    accent: "#3B82F6" },
  r1:                 { label: "R1",                  desc: "Previsite audit technique",      icon: ClipboardCheck, iconGrad: "from-green-500 to-emerald-500",   bgGrad: "from-green-950/60 to-slate-950",   text: "text-green-300",   accent: "#22C55E" },
  r2:                 { label: "R2",                  desc: "Sortie architecte",              icon: Compass,        iconGrad: "from-cyan-500 to-teal-500",       bgGrad: "from-cyan-950/60 to-slate-950",    text: "text-cyan-300",    accent: "#06B6D4" },
  chantiers:          { label: "Chantiers",           desc: "Suivi des travaux",              icon: Truck,          iconGrad: "from-orange-500 to-amber-500",    bgGrad: "from-orange-950/60 to-slate-950",  text: "text-orange-300",  accent: "#F97316" },
  marketing:          { label: "Marketing & Com",     desc: "Acquisition & Communication",    icon: MessageSquare,  iconGrad: "from-rose-500 to-pink-500",       bgGrad: "from-rose-950/60 to-slate-950",    text: "text-rose-300",    accent: "#F43F5E" },
  grands_comptes:     { label: "Grands Comptes",      desc: "CCAS & Prescripteurs",           icon: Briefcase,      iconGrad: "from-amber-500 to-yellow-500",    bgGrad: "from-amber-950/60 to-slate-950",   text: "text-amber-300",   accent: "#F59E0B" },
  communications_ia:  { label: "Communications IA",   desc: "Traitement & Enrichissement",    icon: Mail,           iconGrad: "from-sky-500 to-blue-600",        bgGrad: "from-sky-950/60 to-slate-950",     text: "text-sky-300",     accent: "#0EA5E9" },
  calendrier:         { label: "Calendrier",          desc: "RDV R1 & R2",                    icon: Calendar,       iconGrad: "from-teal-500 to-cyan-500",       bgGrad: "from-teal-950/60 to-slate-950",    text: "text-teal-300",    accent: "#14B8A6" },
  administratif:      { label: "Administratif",       desc: "Gestion des dossiers",           icon: FileText,       iconGrad: "from-slate-400 to-gray-500",      bgGrad: "from-slate-900/80 to-slate-950",   text: "text-slate-300",   accent: "#94A3B8" },
  amo:                { label: "AMO",                 desc: "Assistance maitrise d'ouvrage",  icon: Building2,      iconGrad: "from-violet-500 to-purple-500",   bgGrad: "from-violet-950/60 to-slate-950",  text: "text-violet-300",  accent: "#8B5CF6" },
  formations:         { label: "Formations",          desc: "Competences avec Fatima",        icon: GraduationCap,  iconGrad: "from-purple-500 to-pink-500",     bgGrad: "from-purple-950/60 to-slate-950",  text: "text-purple-300",  accent: "#A855F7" },
  compta_finances:    { label: "Compta / Finances",   desc: "Rapprochements & Stats",         icon: DollarSign,     iconGrad: "from-emerald-500 to-teal-500",    bgGrad: "from-emerald-950/60 to-slate-950", text: "text-emerald-300", accent: "#10B981" },
  compta_fi_new:      { label: "Compta / Finances",    desc: "Validation Pennylane",           icon: Send,           iconGrad: "from-orange-500 to-red-500",      bgGrad: "from-orange-950/60 to-slate-950",  text: "text-orange-300",  accent: "#F97316" },
  comite_direction:   { label: "Comite Direction",    desc: "Echanges directeurs IA",         icon: Crown,          iconGrad: "from-amber-500 to-orange-500",    bgGrad: "from-amber-950/60 to-slate-950",   text: "text-amber-300",   accent: "#F59E0B" },
  super_batard:       { label: "Super Batard",        desc: "Admin & Configuration IA",       icon: Shield,         iconGrad: "from-red-500 to-orange-500",      bgGrad: "from-red-950/60 to-slate-950",     text: "text-red-300",     accent: "#EF4444" },
  tests:              { label: "Tests",               desc: "Environnement de test",          icon: Compass,        iconGrad: "from-purple-500 to-pink-500",     bgGrad: "from-purple-950/60 to-slate-950",  text: "text-purple-300",  accent: "#A855F7" },
  aides_comp:         { label: "Aides Comp.",         desc: "Aides complementaires",          icon: HelpCircle,     iconGrad: "from-pink-500 to-rose-500",       bgGrad: "from-pink-950/60 to-slate-950",    text: "text-pink-300",    accent: "#EC4899" },
  tc_pac_iso:         { label: "TC/PAC ISO",          desc: "Techniques & PAC",               icon: Settings,       iconGrad: "from-indigo-500 to-blue-500",     bgGrad: "from-indigo-950/60 to-slate-950",  text: "text-indigo-300",  accent: "#6366F1" },
  consolidation_v2:   { label: "Compta (Test)",       desc: "Consolidation v2",               icon: BarChart2,      iconGrad: "from-purple-500 to-pink-500",     bgGrad: "from-purple-950/60 to-slate-950",  text: "text-purple-300",  accent: "#A855F7" },
};

const GROUP_ORDER = ["Terrain", "Clients", "Admin & RH", "Finances", "Direction"];
const POLE_GROUPS = {
  r1: "Terrain", r2: "Terrain", chantiers: "Terrain", tc_pac_iso: "Terrain",
  telepro: "Clients", marketing: "Clients", grands_comptes: "Clients", communications_ia: "Clients", calendrier: "Clients",
  administratif: "Admin & RH", amo: "Admin & RH", formations: "Admin & RH", aides_comp: "Admin & RH",
  compta_finances: "Finances", compta_fi_new: "Finances", consolidation_v2: "Finances",
  comite_direction: "Direction", super_batard: "Direction", tests: "Direction",
};

// Default header items — matches DashboardConfigPanel.DEFAULT_SECTIONS.header
const DEFAULT_HEADER_ITEMS = [
  { id: "btn_mon_espace", enabled: true },
  { id: "btn_calendrier", enabled: true },
  { id: "btn_notifications", enabled: true },
  { id: "btn_profil", enabled: true },
  { id: "btn_refresh", enabled: true },
  { id: "widget_sania_banner", enabled: true },
];

export default function UnifiedHeader({
  current,
  onNavigate,
  authUser,
  onMonEspaceClick,
  onCalendrierClick,
  onRefresh,
  refreshing,
  onAddLead,
  onAddDevis,
}) {
  const [polesOpen, setPolesOpen] = useState(false);
  const [headerConfig, setHeaderConfig] = useState(null);
  const dropdownRef = useRef(null);
  const isAdmin = authUser?.email === "sdi@sani-adapt.fr" || authUser?.role === "admin";

  const pole = POLE_IDENTITY[current] || POLE_IDENTITY.tests;
  const PoleIcon = pole.icon;

  // Load config from backend
  useEffect(() => {
    if (!current) return;
    authFetch(`${API}/config/dashboard/${current}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.header) setHeaderConfig(data.header); })
      .catch(() => {});
  }, [current]);

  const isEnabled = (btnId) => {
    const items = headerConfig || DEFAULT_HEADER_ITEMS;
    const item = items.find(i => i.id === btnId);
    return item ? item.enabled : true;
  };

  // Close dropdown
  useEffect(() => {
    const handle = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setPolesOpen(false); };
    if (polesOpen) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [polesOpen]);

  const handleNavigate = (id) => {
    setPolesOpen(false);
    if (id === current) return;
    if (onNavigate) onNavigate(id);
  };

  // Build grouped items for dropdown
  const allPoles = Object.entries(POLE_IDENTITY)
    .filter(([id]) => id !== "home" && POLE_GROUPS[id])
    .filter(([, p]) => !p.adminOnly || isAdmin)
    .map(([id, p]) => ({ id, ...p, group: POLE_GROUPS[id] }));

  const grouped = GROUP_ORDER.map(g => ({
    label: g,
    items: allPoles.filter(i => i.group === g),
  })).filter(g => g.items.length > 0);

  return (
    <header data-testid="unified-header" className="sticky top-0 z-50">
      {/* Main header with pole gradient background */}
      <div className={`bg-gradient-to-r ${pole.bgGrad} border-b border-slate-800/50`}>
        <div className="flex items-center h-20 px-5">
          {/* LEFT: Pole identity */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Icon badge — matches home page tile */}
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pole.iconGrad} flex items-center justify-center shadow-lg`}>
              <PoleIcon className="w-6 h-6 text-white" />
            </div>
            <div className="leading-tight">
              <h1 className={`text-lg font-bold text-white tracking-tight`} data-testid="header-title">
                {pole.label}
              </h1>
              <p className={`text-xs ${pole.text} opacity-80 font-medium`}>
                {pole.desc}
              </p>
            </div>
          </div>

          {/* CENTER: Ticker — scrolling SAN-IA messages */}
          {isEnabled("widget_sania_banner") ? (
            <div className="flex-1 mx-6 min-w-0">
              <InlineTicker current={current} accentColor={pole.accent} />
            </div>
          ) : (
            <div className="flex-1" />
          )}

          {/* RIGHT: Actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Ajouter Lead */}
            {isEnabled("btn_add_lead") && onAddLead && (
              <button
                onClick={onAddLead}
                data-testid="header-add-lead-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-all"
                title="Ajouter un lead"
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden xl:inline">Lead</span>
              </button>
            )}

            {/* Ajouter Devis */}
            {isEnabled("btn_add_devis") && onAddDevis && (
              <button
                onClick={onAddDevis}
                data-testid="header-add-devis-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-lg transition-all"
                title="Ajouter un devis"
              >
                <FilePlus2 className="w-4 h-4" />
                <span className="hidden xl:inline">Devis</span>
              </button>
            )}

            {/* Refresh */}
            {isEnabled("btn_refresh") && onRefresh && (
              <button
                onClick={onRefresh}
                disabled={refreshing}
                data-testid="refresh-btn"
                className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                title="Actualiser"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            )}

            {/* Notifications */}
            {isEnabled("btn_notifications") && (
              <TaskNotificationBadge
                userEmail={authUser?.email}
                onOpenTasks={() => { if (onMonEspaceClick) onMonEspaceClick('taches_internes'); }}
              />
            )}

            {/* Mon Espace — full page navigation */}
            {isEnabled("btn_mon_espace") && (
              <button
                onClick={() => onMonEspaceClick && onMonEspaceClick()}
                data-testid="header-mon-espace-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                <Home className="w-4 h-4 text-blue-400" />
                <span className="hidden lg:inline">Mon Espace</span>
              </button>
            )}

            {/* Separator */}
            <div className="w-px h-8 bg-white/10 mx-1" />

            {/* POLES dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                data-testid="poles-dropdown-btn"
                onClick={() => setPolesOpen(!polesOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-all ${
                  polesOpen
                    ? "bg-white/15 text-white"
                    : "text-slate-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <LayoutGrid className="w-4 h-4" style={{ color: pole.accent }} />
                <span>Poles</span>
                <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${polesOpen ? "rotate-180" : ""}`} />
              </button>

              {polesOpen && (
                <div
                  data-testid="poles-dropdown-panel"
                  className="absolute right-0 top-full mt-2 w-72 bg-slate-900/98 backdrop-blur-2xl border border-slate-700/40 rounded-xl shadow-2xl shadow-black/60 py-2 z-50 overflow-hidden"
                >
                  {/* Accueil */}
                  <div className="px-1.5 pb-1.5 mb-1 border-b border-slate-800/60">
                    <button
                      data-testid="nav-home"
                      onClick={() => handleNavigate("home")}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                        current === "home"
                          ? "bg-amber-500/10 text-white"
                          : "text-slate-300 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center flex-shrink-0">
                        <Home className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold">Accueil</span>
                        <p className="text-[10px] text-slate-500">Selection des poles</p>
                      </div>
                    </button>
                  </div>

                  {/* Grouped poles */}
                  <div className="max-h-[420px] overflow-y-auto px-1.5">
                    {grouped.map((group) => (
                      <div key={group.label} className="mb-1">
                        <div className="px-3 pt-2.5 pb-1 text-[9px] uppercase tracking-[0.15em] text-slate-600 font-bold">
                          {group.label}
                        </div>
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = item.id === current;
                          return (
                            <button
                              key={item.id}
                              data-testid={`nav-${item.id}`}
                              onClick={() => handleNavigate(item.id)}
                              className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs transition-all ${
                                isActive
                                  ? "bg-white/8 text-white font-semibold"
                                  : "text-slate-400 hover:text-white hover:bg-white/5"
                              }`}
                            >
                              <div className={`w-6 h-6 rounded-md bg-gradient-to-br ${item.iconGrad} flex items-center justify-center flex-shrink-0`}>
                                <Icon className="w-3 h-3 text-white" />
                              </div>
                              <div className="flex-1 text-left">
                                <span className="block leading-tight">{item.label}</span>
                                {isActive && <span className="text-[10px] text-slate-500">{item.desc}</span>}
                              </div>
                              {isActive && (
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.accent }} />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Separator */}
            <div className="w-px h-8 bg-white/10 mx-1" />

            {/* Calendrier */}
            {isEnabled("btn_calendrier") && (
              <button
                onClick={() => onCalendrierClick && onCalendrierClick()}
                data-testid="header-calendrier-btn"
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 rounded-lg transition-all"
              >
                <Calendar className="w-4 h-4" />
                <span className="hidden lg:inline">Calendrier</span>
              </button>
            )}

            {/* Profile avatar — existing MonEspaceButton */}
            {isEnabled("btn_profil") && (
              <MonEspaceButton
                user={authUser}
                onLogout={() => onNavigate && onNavigate('home')}
                compact={true}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom accent line — pole color */}
      <div className="h-[2px]" style={{ background: `linear-gradient(90deg, ${pole.accent}, ${pole.accent}60, transparent)` }} />
    </header>
  );
}

// ========== INLINE TICKER (center of header) ==========

const TICKER_ICONS = {
  AlertTriangle, TrendingUp, TrendingDown, Calendar, ArrowRight, Truck, Users, Info,
};

const COLOR_MAP = {
  red: { text: "text-red-400", dot: "bg-red-500" },
  orange: { text: "text-orange-400", dot: "bg-orange-500" },
  green: { text: "text-emerald-400", dot: "bg-emerald-500" },
  blue: { text: "text-blue-400", dot: "bg-blue-500" },
  purple: { text: "text-purple-400", dot: "bg-purple-500" },
  teal: { text: "text-teal-400", dot: "bg-teal-500" },
  slate: { text: "text-slate-400", dot: "bg-slate-500" },
};

function InlineTicker({ current, accentColor }) {
  const [messages, setMessages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasWidget, setHasWidget] = useState(false);
  const [isFatima, setIsFatima] = useState(false);
  const [animState, setAnimState] = useState("visible");

  useEffect(() => {
    if (!current) return;
    const token = localStorage.getItem("auth_token");
    fetch(`${getApiUrl()}/api/config/widgets/${current}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        const active = (data.widgets || []).some(w => w.id === "sania_banner");
        setHasWidget(active);
        if (active) {
          fetch(`${getApiUrl()}/api/widgets/ticker/${current}`, {
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
            .then(r => r.json())
            .then(d => {
              setMessages(d.messages || []);
              setIsFatima(d.fatima === true);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [current]);

  // Cycle through messages
  useEffect(() => {
    if (messages.length <= 1) return;
    const interval = setInterval(() => {
      setAnimState("exiting");
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % messages.length);
        setAnimState("entering");
        setTimeout(() => setAnimState("visible"), 50);
      }, 300);
    }, 5000);
    return () => clearInterval(interval);
  }, [messages.length]);

  if (!hasWidget || messages.length === 0) return null;

  const msg = messages[activeIndex];
  const colors = COLOR_MAP[msg?.color] || COLOR_MAP.slate;
  const IconComp = TICKER_ICONS[msg?.icon] || Info;

  return (
    <div className="flex items-center gap-2.5 bg-white/5 rounded-lg px-4 py-1.5 overflow-hidden">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 animate-pulse ${colors.dot}`} />
      <div
        className={`flex items-center gap-2 min-w-0 transition-all duration-300 ${
          animState === "exiting" ? "opacity-0 -translate-y-1.5" :
          animState === "entering" ? "opacity-0 translate-y-1.5" :
          "opacity-100 translate-y-0"
        }`}
      >
        <IconComp className={`w-4 h-4 flex-shrink-0 ${colors.text}`} />
        <span className={`text-sm font-medium truncate ${colors.text}`}>
          {msg?.text}
        </span>
      </div>
      {messages.length > 1 && (
        <div className="flex items-center gap-0.5 flex-shrink-0 ml-auto">
          {messages.map((_, i) => (
            <button
              key={i}
              onClick={() => { setActiveIndex(i); setAnimState("visible"); }}
              className={`rounded-full transition-all ${
                i === activeIndex ? "w-3 h-1" : "w-1 h-1 opacity-30"
              }`}
              style={{ backgroundColor: i === activeIndex ? accentColor : "#94A3B8" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
